import React, { useState } from "react";

import { useFileUploader } from "@cayuse/shared-react.hooks.use-file-uploader";

import {
    setFormSavingIndicator,
    setFileUploading,
} from "store/actions/form";

import { downloadMultipartFile } from "utils";

import { FORM_EVENTS } from "../../FormResponse/hooks/useFormSave";
import {
    extendFileObjectsForFlowableInput,
    mergeFileArrays,
    prepareFilesToSave,
} from "./utils";

import { MAX_FILE_SIZE_75_MB } from "components/FormAttachmentsView";

import { Loader } from "semantic-ui-react";

import styles from "./styles.css";

const MAX_RETRIES = 3;

export const FileInput = (props) => {
    const { config, onChange, onEvent, Components } = props;

    const [uploading, setUploading] = useState(false);

    const onErrorCallback = () => {
        const files = allFiles.filter((f) => f.completedAt);
		handleChange(files);
		
        setFileUploading(false);
		setUploading(false);
    };

    const fileUploader = useFileUploader({
        onErrorCallback,
        maxRetries: MAX_RETRIES,
        options: {
            restrictions: {
                maxNumberOfFiles: null ,
                maxFileSize: MAX_FILE_SIZE_75_MB
            }
        }
    });

    const allFiles = extendFileObjectsForFlowableInput(
        mergeFileArrays(config.value, fileUploader.files)
    );
    const completedCount = allFiles.filter((f) => f.completedAt).length;

    const handleChange = (value) => {
        // save files to the form
        onEvent(FORM_EVENTS.FOCUS, { $path: config.$path }, {});
        onChange(value);
    };

    React.useEffect(() => {
        const loadDownloadUrls = async () => {
            setFormSavingIndicator(true);
            handleChange(prepareFilesToSave(allFiles));

            // remove files from the uploader when they were uploaded to aws
            allFiles.forEach((f) => {
                fileUploader.removeFile(f);
            });
        };

        if (
            fileUploader.files.length > 0 &&
            completedCount === allFiles.length
        ) {
            // when all new files uploaded are completed and has downloadUrl - save them to the form
            loadDownloadUrls();
            setFileUploading(false);
			setUploading(false);
        }
    }, [completedCount]);

    React.useEffect(() => {
        if (fileUploader.error || allFiles.find((f) => f.error?.length > 0)) {
            setFormSavingIndicator(false);
        }
    }, [allFiles]);

    const handleRemoveUploadingFiles = (files) => {
        allFiles.filter((f) => !files.find((file) => file.name === f.name))
            .filter((f) => f.status === 6)
            .forEach((f) => {
                fileUploader.removeFile(f);
            });
    };

    const containsFile = (file, rejectedFiles) => {
        return rejectedFiles.some((f) => f.name === file.name && f.size === file.size);
    };

    const handleRemove = (files, rejectedFiles) => {
        // remove file from the uploader hook when the last file was removed from the input
        if (files.length === 0) {
            allFiles.forEach((f) => {
                fileUploader.removeFile(f);
            });
            // reset the upload state
            setFileUploading(false);
            setUploading(false);
        } else {
            // remove files from the uploader hook when they were removed from the input
            files.forEach((f) => {
                if (f.status === 1) {
                    fileUploader.removeFile(f);
                }
            });

            // remove files that are in the process of uploading
            handleRemoveUploadingFiles(files);

            // if all remaining files are completed, reset the upload state
            if (completedCount === files.length) {
                setFileUploading(false);
                setUploading(false);
            }
        }

        handleChange(files.filter((f) => f.status !== 1 && !containsFile(f, rejectedFiles)));
    };

    const existsInFileUploader = (fileName) => {
        return fileUploader.files.find((f) => f.name === fileName);
    };

    const separateNewAndExistingFiles = (files) => {
        return files
            .filter(f => !f.id && !f.completedAt)
            .reduce(([ newFiles, existingFiles ], f) => {
                const targetList = existsInFileUploader(f.name) ? existingFiles : newFiles;
				targetList.push(Object.assign(f.fileData, { wrapper: () => f }));
                return [ newFiles, existingFiles ];
            }, [ [], [] ]);
    };

    const onInputChange = (files) => {
        let removeFiles = true;

        const [ newUploadFiles, existingUploadFiles ] = separateNewAndExistingFiles(files);
        const rejectedFiles = [];

        // start upload files and save them into the form if they are not uploaded yet
        fileUploader.addFiles(newUploadFiles, (acceptedFiles) => {
            if (acceptedFiles.length > 0) {
                setFormSavingIndicator(true);

                removeFiles = false;
                setFileUploading(true);
				setUploading(true);
                handleChange(
                    files
                        .filter((f) => f.id)
                        .concat(existingUploadFiles.map((f) => f.wrapper()))
                        .concat(acceptedFiles.map((f) => f.wrapper()))
                );
            } else {
                handleChange(files.filter((f) => f.id));
            }
        }, (invalidExtensionFiles, exceedSizeFiles) => {
            rejectedFiles.push(
                ...invalidExtensionFiles.map((f) => f.wrapper()),
                ...exceedSizeFiles.map((f) => f.wrapper()));
        });

        if (removeFiles) {
            handleRemove(files, rejectedFiles);
        }

        resetFileInputValue();
        setTimeout(() => addA11yTextsToUploader());
    };

    const addA11yTextsToUploader = () => {
        const a11yTextForUploaderAction = "Press enter to select from file system.";

        if (containerRef.current && containerRef.current.querySelector(`[tabindex="0"]`)) {
            containerRef.current.setAttribute("role", "application");

            const uploaderWrapper = containerRef.current.querySelector(`[tabindex="0"]`);
            const uploaderLabel = containerRef.current.querySelector(".flw__label");

            let selectedFiles = [];

            if (uploaderWrapper.querySelectorAll(".flw__fileCard").length > 0) {
                uploaderWrapper.querySelectorAll(".flw__fileCard").forEach(({ children }) => {
                    const label = children[0].textContent;
                    const size = children[1].textContent;
                    const fileDescription = `${label} ${size}`;

                    selectedFiles.push(fileDescription);
                });
            }

            const a11yTextForSelections = selectedFiles.length > 0 ? `${selectedFiles.join(",")} selected.` : "";

            let a11yTextForUploader = "";

            if (uploaderLabel.classList.contains("flw__upload--required__label")) {
                a11yTextForUploader = `${uploaderLabel.textContent}, required. ${a11yTextForSelections} ${a11yTextForUploaderAction}`;
            } else {
                a11yTextForUploader = `${uploaderLabel.textContent}. ${a11yTextForSelections} ${a11yTextForUploaderAction}`;
            }

            uploaderWrapper.setAttribute("aria-label", a11yTextForUploader);
            uploaderWrapper.setAttribute("role", "button");

            if (containerRef.current.querySelector("a.flw__fileCard__fileAction")) {
                const removeFileButtons = containerRef.current.querySelectorAll("a.flw__fileCard__fileAction");

                if (removeFileButtons.length > 0) {
                    removeFileButtons.forEach(button => {
                        if (button.querySelector("svg")) {
                            button.setAttribute("aria-label", "Remove file from selections.");
                            button.setAttribute("role", "button");
                            button.removeAttribute("tabindex");
                        } else {
                            button.setAttribute("tabindex", "-1");
                        }
                    });
                }
            }
        }
    };

    // reset the file input value to allow selecting the same file again (bypassing the Chrome limitation)
    const resetFileInputValue = () => {
        if (containerRef.current) {
            const fileInput = containerRef.current.querySelector("input[type='file']");
            if (fileInput) {
                fileInput.value = "";
            }
        }
    };

    const containerRef = React.useRef(null);

    React.useEffect(() => {
        if (containerRef.current) {
            const links = containerRef.current.querySelectorAll("a[download]");

            const onDownload = (e) => {
                const href = e.target.href;

                if (href.startsWith("platform://")) {
                    e.preventDefault();
                    e.stopPropagation();

                    downloadMultipartFile({
                        id: href.slice(11),
                        fileName: e.target.innerText,
                    });
                }
            };

            if (links.length > 0) {
                links.forEach(link => {
                    const label = link.textContent;
                    const size = link.parentElement.querySelector(".flw__fileCard__fileSize").textContent;
                    
                    link.setAttribute("aria-label", `File name: ${label}; Size: ${size}; Press enter to download this file.`);
                    link.setAttribute("role", "button");
    
                    link.addEventListener("click", onDownload);
                    link.addEventListener("keydown", function (e) {
                        if (e.key === "Enter") {
                            onDownload(e);
                        }
                    });
                });
            }

            return () => {
                if (links.length > 0) {
                    links.forEach(link => {
                        link.removeEventListener("click", onDownload);
                        link.removeEventListener("keydown", onDownload);
                    });
                }
            };
        }
    }, [config.value]);

    React.useEffect(() => {
        addA11yTextsToUploader();
    }, [config]);

    return (
        <div ref={containerRef} className={styles.fileUploader}>
            {uploading && <Loader active size="tiny" inline />}
            <Components.upload {...props} onChange={onInputChange} />
        </div>
    );
};
